import { useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { List } from '~/components/component-library'
import { TokenBalance } from '~/components/component-library/TokenBalance'
import { TokenDisplay } from '~/components/component-library/TokenDisplay'
import { useCreatePositionClaimTransaction } from '~/components/smart-accounts/positions/hooks/useCreatePositionClaimTransaction'
import { usePositionCallsToClaim } from '~/components/smart-accounts/positions/hooks/usePositionCallsToClaim'
import {
  PositionClaimFormValues,
  positionClaimSchema,
} from '~/components/smart-accounts/positions/positionClaimSchema'
import { PositionBalance } from '~/components/smart-accounts/positions/types'
import {
  buildPositionClaimData,
  getPositionClaimTitle,
  getPositionTypeLabel,
} from '~/components/smart-accounts/positions/utils'
import { TransactionDialogContent } from '~/components/smart-accounts/transactions/TransactionDialogContent'
import { createTransactionHandlers } from '~/components/smart-accounts/transactions/createTransactionHandlers'
import { QUERY_KEYS } from '~/constants/react-query'
import { useRequireMemo } from '~/hooks/org'
import { useActiveOrgId } from '~/hooks/org/useActiveOrgId'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import {
  buildAddToBatchDropdownOptions,
  useAddCurrentFormToBatch,
} from '~/hooks/useAddCurrentFormToBatch'
import { useOrgSmartAccount } from '~/hooks/useSmartAccounts'
import { useCanAddToBatch } from '~/hooks/useTransactionBatch'
import {
  BackendLoggingData,
  BasicTransactionType,
  MetadataType,
  OrgTransaction,
} from '~/types'

export const PositionClaimDialog = ({
  positionBalance,
  setIsOpen,
}: {
  positionBalance: PositionBalance
  setIsOpen: (isOpen: boolean) => void
}) => {
  const { data: callsToClaim, isLoading: isLoadingCallsToClaim } =
    usePositionCallsToClaim(positionBalance)

  const methods = useForm<PositionClaimFormValues>({
    defaultValues: {
      account: positionBalance.accountAddress,
      chainId: positionBalance.chainId,
      calls: callsToClaim ?? [],
      memo: getPositionClaimTitle(positionBalance),
    },
    mode: 'onChange',
  })

  const { smartAccount: account } = useOrgSmartAccount(
    positionBalance.accountAddress,
  )

  useEffect(() => {
    if (callsToClaim) {
      methods.setValue('calls', callsToClaim)
    }
  }, [callsToClaim, methods])

  const {
    approveAndSubmitTransaction,
    isProcessing: isProcessingApproveAndSubmit,
  } = useApproveAndSubmitTransaction()

  const activeOrgId = useActiveOrgId()

  const { createTransactionOnSubmit, isProcessingTransaction } =
    useCreatePositionClaimTransactionSubmit({
      activeOrgId,
      setIsOpen,
      positionBalance,
    })

  const memo = useWatch({ control: methods.control, name: 'memo' })
  const requireMemo = useRequireMemo()

  const claimTitle = getPositionClaimTitle(positionBalance)
  const protocolLabel = getPositionTypeLabel(positionBalance.positionType)

  const canAddToBatch = useCanAddToBatch({
    smartAccountId: account?.id,
    chainId: positionBalance.chainId,
  })
  const { addCurrentFormToBatch, isProcessing: isAddingToBatch } =
    useAddCurrentFormToBatch()

  const handleAddToBatch = async () => {
    if (!account || !callsToClaim?.length) return
    await addCurrentFormToBatch({
      smartAccountId: account.id,
      chainId: positionBalance.chainId,
      title: claimTitle,
      types: [BasicTransactionType.Claim],
      calls: callsToClaim,
      metadata: [
        {
          type: MetadataType.PositionClaim,
          ...buildPositionClaimData({
            positionBalance,
            chainId: positionBalance.chainId,
          }),
        },
      ],
      memo: methods.getValues('memo'),
      setIsOpen,
    })
  }

  if (!account) return null

  const transactionHandlers =
    createTransactionHandlers<PositionClaimFormValues>({
      methods,
      senderAccount: account,
      getLoggingData: () => ({
        positionId: positionBalance.id,
        positionType: positionBalance.positionType,
        positionChainId: positionBalance.chainId,
        positionAccount: positionBalance.accountAddress,
        positionAccountId: positionBalance.smartAccountId,
        positionUsdValue:
          positionBalance.feesUsdValue !== null
            ? positionBalance.feesUsdValue.toString()
            : positionBalance.usdValue !== null
              ? positionBalance.usdValue.toString()
              : '',
        positionVersion: positionBalance.version,
      }),
      createTransactionOnSubmit,
      approveAndSubmitTransaction,
    })

  const isValidForm =
    positionClaimSchema.safeParse(methods.getValues()).success &&
    !(requireMemo && !memo?.trim())

  return (
    <FormProvider {...methods}>
      <TransactionDialogContent
        id="claimPositionBalance"
        title={claimTitle}
        isLoading={
          isProcessingTransaction ||
          isProcessingApproveAndSubmit ||
          isLoadingCallsToClaim
        }
        isValidForm={isValidForm}
        handlers={transactionHandlers}
        smartAccountAddress={positionBalance.accountAddress}
        chainId={positionBalance.chainId}
        eventType="Claim"
        transactionTypes={[BasicTransactionType.Claim]}
        addressesToWatch={[positionBalance.accountAddress]}
        reviewDropdownOptions={buildAddToBatchDropdownOptions({
          canAddToBatch,
          isDisabled: !isValidForm || isAddingToBatch,
          onClick: handleAddToBatch,
        })}
      >
        <div className="flex flex-col space-y-4">
          <List
            label="Claim amounts"
            items={positionBalance.tokens.map((token) => ({
              key: token.address,
              content: (
                <TokenDisplay
                  address={token.address}
                  chainId={positionBalance.chainId}
                  layout="two-line"
                  protocol={protocolLabel}
                />
              ),
              meta: (
                <TokenBalance
                  address={token.address}
                  chainId={positionBalance.chainId}
                  tokenAmount={token.claimableAmount}
                  layout="standard"
                  size="base"
                  direction="neutral"
                />
              ),
            }))}
          />
        </div>
      </TransactionDialogContent>
    </FormProvider>
  )
}

const useCreatePositionClaimTransactionSubmit = ({
  activeOrgId,
  setIsOpen,
  positionBalance,
}: {
  activeOrgId?: string
  setIsOpen: (isOpen: boolean) => void
  positionBalance: PositionBalance
}) => {
  const queryClient = useQueryClient()
  const { createClaimTransaction, isProcessing: isProcessingTransaction } =
    useCreatePositionClaimTransaction()

  const createTransactionOnSubmit = useCallback(
    (
      dontOpenDialog: boolean,
      callback?: (transaction: OrgTransaction) => void,
    ) =>
      async ({
        data,
        loggingData,
      }: {
        data: PositionClaimFormValues
        loggingData?: BackendLoggingData
      }) => {
        await createClaimTransaction(
          {
            claimForm: data,
            positionBalance,
            loggingData,
          },
          dontOpenDialog,
          (transaction) => {
            queryClient.setQueryData(
              [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
              transaction,
            )
            setIsOpen(false)
            callback?.(transaction)
          },
        )
      },
    [
      createClaimTransaction,
      positionBalance,
      queryClient,
      activeOrgId,
      setIsOpen,
    ],
  )

  return {
    createTransactionOnSubmit,
    isProcessingTransaction,
  }
}
